import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, RefreshCw, History, Edit2, Trash2,
  X, Check, Droplets, Type, Image, FileText,
  Maximize2, Grid, Star, Camera, Clock, MessageSquare,
} from 'lucide-react';
import { brandStandardsService, KNOWN_SECTIONS } from '../api/brandStandardsApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import { toast } from 'react-hot-toast';

// ── Section metadata ──────────────────────────────────────────────────────────
const SECTION_META = {
  colors:       { label: 'Colors',       icon: Droplets     },
  typography:   { label: 'Typography',   icon: Type         },
  logos:        { label: 'Logos',        icon: Image        },
  voice:        { label: 'Voice & Tone', icon: MessageSquare },
  poster_rules: { label: 'Poster Rules', icon: FileText     },
  spacing:      { label: 'Spacing',      icon: Maximize2    },
  iconography:  { label: 'Iconography',  icon: Grid         },
  imagery:      { label: 'Imagery',      icon: Camera       },
};

const sectionLabel = (s) =>
  SECTION_META[s]?.label ||
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const SectionIcon = ({ section, ...props }) => {
  const Icon = SECTION_META[section]?.icon || FileText;
  return <Icon {...props} />;
};

const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

// ── Palette display ───────────────────────────────────────────────────────────
const PaletteDisplay = ({ palette }) => {
  if (!palette?.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {palette.map((c, i) => (
        <div
          key={i}
          className="rounded-xl overflow-hidden border"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
        >
          <div className="h-16" style={{ backgroundColor: c.hex || c.color || '#e5e7eb' }} />
          <div className="p-3">
            <p className="text-xs font-semibold truncate" style={{ color: NEUTRAL_COLORS.black }}>
              {c.name || 'Unnamed'}
            </p>
            <p className="text-xs font-mono mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
              {c.hex || c.color || ''}
            </p>
            {c.usage && (
              <p className="text-xs mt-1 leading-relaxed" style={{ color: SYSTEM_COLORS.textSoft }}>
                {c.usage}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Typography display ────────────────────────────────────────────────────────
const TypographyDisplay = ({ typography }) => {
  if (!typography?.length) return null;
  return (
    <div className="space-y-3">
      {typography.map((t, i) => (
        <div
          key={i}
          className="rounded-xl border p-4 flex items-start gap-4"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
        >
          <div
            className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl"
            style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}
          >
            <span
              style={{
                fontFamily: t.family || 'inherit',
                fontWeight: t.weight || 'normal',
                fontSize: '26px',
                color: BRAND_COLORS.primary,
                lineHeight: 1,
              }}
            >
              Aa
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
              {t.name || 'Unnamed'}
            </p>
            <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
              {[t.family, t.weight && `Weight ${t.weight}`, t.size].filter(Boolean).join(' · ')}
            </p>
            {t.usage && (
              <p className="text-xs mt-1 leading-relaxed" style={{ color: SYSTEM_COLORS.textSoft }}>
                {t.usage}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Palette builder ───────────────────────────────────────────────────────────
const PaletteBuilder = ({ value, onChange }) => {
  const add    = ()           => onChange([...value, { name: '', hex: '#37559e', usage: '' }]);
  const remove = (i)          => onChange(value.filter((_, idx) => idx !== i));
  const update = (i, k, v)   => onChange(value.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: SYSTEM_COLORS.textMuted }}>
          Palette
        </span>
        <button type="button" onClick={add}
          className="inline-flex items-center gap-1 text-xs font-semibold"
          style={{ color: BRAND_COLORS.primary }}>
          <Plus size={12} /> Add Colour
        </button>
      </div>
      {value.map((c, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="color"
            value={c.hex || '#37559e'}
            onChange={(e) => update(i, 'hex', e.target.value)}
            className="h-8 w-8 rounded-lg cursor-pointer flex-shrink-0"
            style={{ border: `1px solid ${SYSTEM_COLORS.borderSoft}`, padding: '1px' }}
          />
          <input
            type="text"
            placeholder="Name"
            value={c.name}
            onChange={(e) => update(i, 'name', e.target.value)}
            className="flex-1 rounded-xl border px-2.5 py-1.5 text-xs outline-none"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: NEUTRAL_COLORS.black }}
          />
          <input
            type="text"
            placeholder="Usage"
            value={c.usage}
            onChange={(e) => update(i, 'usage', e.target.value)}
            className="flex-1 rounded-xl border px-2.5 py-1.5 text-xs outline-none"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: NEUTRAL_COLORS.black }}
          />
          <button type="button" onClick={() => remove(i)} className="flex-shrink-0 p-1 rounded-lg hover:bg-red-50">
            <X size={13} style={{ color: SYSTEM_COLORS.danger }} />
          </button>
        </div>
      ))}
    </div>
  );
};

// ── Typography builder ────────────────────────────────────────────────────────
const TypographyBuilder = ({ value, onChange }) => {
  const add    = ()         => onChange([...value, { name: '', family: '', weight: '400', size: '', usage: '' }]);
  const remove = (i)        => onChange(value.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(value.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));

  const inp = "rounded-xl border px-2.5 py-1.5 text-xs outline-none w-full";
  const inpStyle = { borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: NEUTRAL_COLORS.black };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: SYSTEM_COLORS.textMuted }}>
          Typography
        </span>
        <button type="button" onClick={add}
          className="inline-flex items-center gap-1 text-xs font-semibold"
          style={{ color: BRAND_COLORS.primary }}>
          <Plus size={12} /> Add Font
        </button>
      </div>
      {value.map((t, i) => (
        <div
          key={i}
          className="rounded-xl border p-3 space-y-2"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
        >
          <div className="flex items-center gap-2">
            <input type="text" placeholder="Name (e.g. Heading)" value={t.name}
              onChange={(e) => update(i, 'name', e.target.value)}
              className={`${inp} flex-1`} style={inpStyle} />
            <button type="button" onClick={() => remove(i)} className="flex-shrink-0 p-1 rounded-lg hover:bg-red-50">
              <X size={13} style={{ color: SYSTEM_COLORS.danger }} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input type="text" placeholder="Font family" value={t.family}
              onChange={(e) => update(i, 'family', e.target.value)}
              className={inp} style={inpStyle} />
            <input type="text" placeholder="Weight (e.g. 700)" value={t.weight}
              onChange={(e) => update(i, 'weight', e.target.value)}
              className={inp} style={inpStyle} />
            <input type="text" placeholder="Size (e.g. 24px)" value={t.size}
              onChange={(e) => update(i, 'size', e.target.value)}
              className={inp} style={inpStyle} />
          </div>
          <input type="text" placeholder="Usage notes" value={t.usage}
            onChange={(e) => update(i, 'usage', e.target.value)}
            className={inp} style={inpStyle} />
        </div>
      ))}
    </div>
  );
};

// ── Publish / Edit Modal ──────────────────────────────────────────────────────
const EMPTY = {
  section: '', title: '', version: '1.0',
  content: '', isCurrent: true, palette: [], typography: [],
};

const PublishModal = ({ initial, defaultSection, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? { ...EMPTY, ...initial, palette: initial.palette || [], typography: initial.typography || [] }
      : { ...EMPTY, section: defaultSection || '' }
  );
  const [saving, setSaving] = useState(false);
  const [customSection, setCustomSection] = useState('');

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const resolvedSection = form.section === '__custom__' ? customSection : form.section;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resolvedSection.trim()) { toast.error('Section is required'); return; }
    if (!form.title.trim())      { toast.error('Title is required');   return; }
    setSaving(true);
    try {
      const payload = {
        section:    resolvedSection.trim().toLowerCase().replace(/\s+/g, '_'),
        title:      form.title.trim(),
        version:    form.version.trim() || undefined,
        content:    form.content.trim() || undefined,
        isCurrent:  form.isCurrent,
        palette:    form.palette.length    ? form.palette    : undefined,
        typography: form.typography.length ? form.typography : undefined,
      };
      const saved = isEdit
        ? await brandStandardsService.update(initial.id, payload)
        : await brandStandardsService.create(payload);
      toast.success(isEdit ? 'Section updated' : 'Section published');
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const lbl = "block text-xs font-semibold mb-1.5";
  const inp = "w-full rounded-xl border px-3 py-2 text-sm outline-none";
  const inpStyle = { borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: NEUTRAL_COLORS.black };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl shadow-xl my-8"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}
      >
        {/* header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}
        >
          <h2 className="text-base font-bold" style={{ color: NEUTRAL_COLORS.black }}>
            {isEdit ? 'Edit Section' : 'Publish Brand Section'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X size={18} style={{ color: SYSTEM_COLORS.textMuted }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Section */}
            <div className="sm:col-span-2">
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Section *</label>
              {isEdit ? (
                <p
                  className="text-sm font-semibold px-3 py-2 rounded-xl capitalize"
                  style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: NEUTRAL_COLORS.black }}
                >
                  {sectionLabel(form.section)}
                </p>
              ) : (
                <>
                  <select
                    value={form.section}
                    onChange={(e) => set('section', e.target.value)}
                    className={inp} style={inpStyle}
                  >
                    <option value="">Choose section…</option>
                    {KNOWN_SECTIONS.map((s) => (
                      <option key={s} value={s}>{sectionLabel(s)}</option>
                    ))}
                    <option value="__custom__">Custom…</option>
                  </select>
                  {form.section === '__custom__' && (
                    <input
                      type="text"
                      placeholder="section_slug (lowercase, underscores)"
                      value={customSection}
                      onChange={(e) => setCustomSection(e.target.value)}
                      className={`${inp} mt-2`} style={inpStyle}
                    />
                  )}
                </>
              )}
            </div>

            {/* Version */}
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Version</label>
              <input
                type="text"
                value={form.version}
                onChange={(e) => set('version', e.target.value)}
                placeholder="1.0"
                className={inp} style={inpStyle}
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. UDSM Colour Palette 2026"
              className={inp} style={inpStyle}
            />
          </div>

          {/* Content */}
          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Content / Guidelines</label>
            <textarea
              value={form.content}
              onChange={(e) => set('content', e.target.value)}
              rows={5}
              placeholder="Brand guidelines, usage rules, notes…"
              className={inp}
              style={{ ...inpStyle, resize: 'vertical' }}
            />
          </div>

          {/* isCurrent toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              onClick={() => set('isCurrent', !form.isCurrent)}
              className="relative w-9 h-5 rounded-full transition-colors flex-shrink-0"
              style={{ backgroundColor: form.isCurrent ? BRAND_COLORS.primary : SYSTEM_COLORS.surfaceMuted }}
            >
              <div
                className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all duration-150"
                style={{ left: form.isCurrent ? '18px' : '2px' }}
              />
            </div>
            <span className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
              Set as current version
            </span>
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              (demotes previous current for this section)
            </span>
          </label>

          {/* Palette builder */}
          <div className="rounded-xl border p-4 space-y-3"
               style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
            <PaletteBuilder value={form.palette} onChange={(v) => set('palette', v)} />
          </div>

          {/* Typography builder */}
          <div className="rounded-xl border p-4 space-y-3"
               style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
            <TypographyBuilder value={form.typography} onChange={(v) => set('typography', v)} />
          </div>

          {/* Actions */}
          <div
            className="flex justify-end gap-3 pt-2 border-t"
            style={{ borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-xl border"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm font-semibold rounded-xl text-white disabled:opacity-50"
              style={{ backgroundColor: BRAND_COLORS.primary }}
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── BrandStandards ────────────────────────────────────────────────────────────
const BrandStandards = () => {
  const [items,         setItems]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [showHistory,   setShowHistory]   = useState(false);
  const [historyItems,  setHistoryItems]  = useState([]);
  const [historyLoading,setHistoryLoading]= useState(false);
  const [publishModal,  setPublishModal]  = useState(null); // null | 'new' | 'new-version' | item
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingId,    setDeletingId]    = useState(null);

  const fetchSections = useCallback(async (keepSection = false) => {
    setLoading(true);
    try {
      const data = await brandStandardsService.list();
      const list = data || [];
      setItems(list);
      if (!keepSection) {
        const first = list[0]?.section ?? null;
        setActiveSection((prev) => prev ?? first);
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to load brand standards');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSections(); }, []);

  const fetchHistory = async (section) => {
    setHistoryLoading(true);
    try {
      const all = await brandStandardsService.list({ includeHistory: true });
      setHistoryItems((all || []).filter((i) => i.section === section));
    } catch {
      toast.error('Failed to load version history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setShowHistory(false);
    setDeleteConfirm(null);
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && activeSection) fetchHistory(activeSection);
  };

  const handleSaved = (saved) => {
    setItems((prev) => {
      const exists = prev.find((i) => i.id === saved.id);
      if (exists) {
        return prev.map((i) => {
          if (i.id === saved.id) return saved;
          if (saved.isCurrent && i.section === saved.section && i.id !== saved.id)
            return { ...i, isCurrent: false };
          return i;
        });
      }
      const demoted = saved.isCurrent
        ? prev.map((i) => (i.section === saved.section ? { ...i, isCurrent: false } : i))
        : [...prev];
      return [...demoted, saved];
    });
    setActiveSection(saved.section);
    if (showHistory) fetchHistory(saved.section);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await brandStandardsService.delete(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setHistoryItems((prev) => prev.filter((i) => i.id !== id));
      toast.success('Version deleted');
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  // Unique sections in order of first appearance
  const sections = [...new Map(items.map((i) => [i.section, true])).keys()];
  const current  = items.find((i) => i.section === activeSection && i.isCurrent)
                ?? items.find((i) => i.section === activeSection);

  const DeleteButtons = ({ id }) =>
    deleteConfirm === id ? (
      <div className="flex items-center gap-1">
        <button
          onClick={() => handleDelete(id)}
          disabled={deletingId === id}
          className="p-1.5 rounded-lg text-white disabled:opacity-50"
          style={{ backgroundColor: SYSTEM_COLORS.danger }}
        >
          <Check size={12} />
        </button>
        <button
          onClick={() => setDeleteConfirm(null)}
          className="p-1.5 rounded-lg"
          style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}
        >
          <X size={12} style={{ color: SYSTEM_COLORS.textMuted }} />
        </button>
      </div>
    ) : (
      <button
        onClick={() => setDeleteConfirm(id)}
        className="p-1.5 rounded-lg hover:bg-red-50 border"
        style={{ borderColor: SYSTEM_COLORS.borderSoft }}
      >
        <Trash2 size={12} style={{ color: SYSTEM_COLORS.danger }} />
      </button>
    );

  return (
    <div className="space-y-5">

      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <span className="text-xl font-bold" style={{ color: BRAND_COLORS.primary }}>{sections.length}</span>
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>Sections</span>
          </div>
          <div
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <span className="text-xl font-bold" style={{ color: BRAND_COLORS.primary }}>{items.length}</span>
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>Versions</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchSections(true)}
            className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted, backgroundColor: SYSTEM_COLORS.shellSurface }}
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={() => setPublishModal('new')}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            <Plus size={13} /> Publish Section
          </button>
        </div>
      </div>

      {/* ── Loading ──────────────────────────────────────────────────── */}
      {loading ? (
        <div
          className="flex items-center justify-center h-48 rounded-xl border"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}
        >
          <div className="h-8 w-8 animate-spin rounded-full border-b-2"
               style={{ borderColor: BRAND_COLORS.primary }} />
        </div>

      ) : sections.length === 0 ? (
        /* ── Empty state ──────────────────────────────────────────── */
        <div
          className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-24"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}
        >
          <FileText size={36} style={{ color: SYSTEM_COLORS.textSoft }} />
          <p className="text-sm font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
            No brand standards published yet
          </p>
          <button
            onClick={() => setPublishModal('new')}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            <Plus size={14} /> Publish First Section
          </button>
        </div>

      ) : (
        <>
          {/* ── Section nav pills ──────────────────────────────────── */}
          <div className="flex overflow-x-auto gap-2 pb-1">
            {sections.map((section) => {
              const active = section === activeSection;
              return (
                <button
                  key={section}
                  onClick={() => handleSectionChange(section)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-colors"
                  style={{
                    backgroundColor: active ? BRAND_COLORS.primary : SYSTEM_COLORS.shellSurface,
                    color: active ? '#fff' : SYSTEM_COLORS.textMuted,
                    border: `1px solid ${active ? 'transparent' : SYSTEM_COLORS.borderSoft}`,
                  }}
                >
                  <SectionIcon section={section} size={14} />
                  {sectionLabel(section)}
                </button>
              );
            })}
          </div>

          {/* ── Section card ───────────────────────────────────────── */}
          {current ? (
            <div
              className="rounded-xl border overflow-hidden"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
            >
              {/* Card header */}
              <div
                className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between px-5 py-4 border-b"
                style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold" style={{ color: NEUTRAL_COLORS.black }}>
                      {current.title}
                    </h3>
                    {current.version && (
                      <span
                        className="rounded-full px-2.5 py-0.5 text-xs font-semibold font-mono"
                        style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
                      >
                        v{current.version}
                      </span>
                    )}
                    {current.isCurrent && (
                      <span
                        className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                        style={{ backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success }}
                      >
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-1" style={{ color: SYSTEM_COLORS.textMuted }}>
                    Updated {fmt(current.updatedAt)}
                    {current.updatedBy ? ` · ${current.updatedBy}` : ''}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  <button
                    onClick={toggleHistory}
                    className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium"
                    style={{
                      borderColor: showHistory ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft,
                      color:       showHistory ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                      backgroundColor: showHistory ? SYSTEM_COLORS.primaryTint : 'transparent',
                    }}
                  >
                    <History size={12} /> History
                  </button>
                  <button
                    onClick={() => setPublishModal(current)}
                    className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium"
                    style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}
                  >
                    <Edit2 size={12} /> Edit
                  </button>
                  <button
                    onClick={() => setPublishModal('new-version')}
                    className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-white"
                    style={{ backgroundColor: BRAND_COLORS.primary }}
                  >
                    <Plus size={12} /> New Version
                  </button>
                  <DeleteButtons id={current.id} />
                </div>
              </div>

              {/* Card body */}
              <div className="p-5 space-y-6">
                {current.content && (
                  <p
                    className="text-sm leading-relaxed whitespace-pre-wrap"
                    style={{ color: NEUTRAL_COLORS.black }}
                  >
                    {current.content}
                  </p>
                )}

                {current.palette?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide"
                       style={{ color: SYSTEM_COLORS.textMuted }}>
                      Palette
                    </p>
                    <PaletteDisplay palette={current.palette} />
                  </div>
                )}

                {current.typography?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide"
                       style={{ color: SYSTEM_COLORS.textMuted }}>
                      Typography
                    </p>
                    <TypographyDisplay typography={current.typography} />
                  </div>
                )}

                {!current.content && !current.palette?.length && !current.typography?.length && (
                  <div className="flex flex-col items-center justify-center gap-2 py-12">
                    <FileText size={28} style={{ color: SYSTEM_COLORS.textSoft }} />
                    <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                      No content yet — click Edit to add guidelines.
                    </p>
                  </div>
                )}
              </div>

              {/* Version history panel */}
              {showHistory && (
                <div
                  className="border-t px-5 py-4 space-y-3"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
                >
                  <p
                    className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5"
                    style={{ color: SYSTEM_COLORS.textMuted }}
                  >
                    <Clock size={12} /> Version history — {sectionLabel(activeSection)}
                  </p>
                  {historyLoading ? (
                    <div className="flex items-center justify-center h-16">
                      <div className="h-5 w-5 animate-spin rounded-full border-b-2"
                           style={{ borderColor: BRAND_COLORS.primary }} />
                    </div>
                  ) : historyItems.length === 0 ? (
                    <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                      No history available.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {historyItems.map((h) => (
                        <div
                          key={h.id}
                          className="flex items-center justify-between px-3 py-2.5 rounded-xl border"
                          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
                        >
                          <div className="min-w-0 mr-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
                                {h.title}
                              </span>
                              {h.version && (
                                <span
                                  className="text-xs font-mono px-1.5 py-0.5 rounded"
                                  style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}
                                >
                                  v{h.version}
                                </span>
                              )}
                              {h.isCurrent && (
                                <span
                                  className="rounded-full px-2 py-0.5 text-xs font-semibold"
                                  style={{ backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success }}
                                >
                                  Current
                                </span>
                              )}
                            </div>
                            <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                              {fmt(h.updatedAt)}{h.updatedBy ? ` · ${h.updatedBy}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => setPublishModal(h)}
                              className="p-1.5 rounded-lg border hover:bg-white"
                              style={{ borderColor: SYSTEM_COLORS.borderSoft }}
                            >
                              <Edit2 size={12} style={{ color: SYSTEM_COLORS.textMuted }} />
                            </button>
                            <DeleteButtons id={h.id} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-16"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }}
            >
              <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                No current version for this section.
              </p>
              <button
                onClick={() => setPublishModal('new-version')}
                className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: BRAND_COLORS.primary }}
              >
                <Plus size={14} /> Publish Version
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Modal ──────────────────────────────────────────────────────── */}
      {publishModal && (
        <PublishModal
          initial={
            publishModal === 'new' || publishModal === 'new-version'
              ? null
              : publishModal
          }
          defaultSection={
            publishModal === 'new-version' ? activeSection : null
          }
          onClose={() => setPublishModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default BrandStandards;
